import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import {
  SITE,
  DEFAULT_DESCRIPTION,
  NOINDEX_PATTERNS,
  buildTitle,
  absoluteUrl,
  getPageSeo,
} from '../../constants/seo';

/**
 * Per-page document head: title, description, canonical, Open Graph, Twitter
 * card, and optional JSON-LD.
 *
 * Static pages can render it bare — it falls back to the PAGE_SEO entry for the
 * current route. Dynamic pages pass their own values in.
 *
 * @param {string}  [title]        Page title without the brand suffix.
 * @param {string}  [description]  Meta description, ~150-160 characters.
 * @param {string}  [path]         Canonical path; defaults to the current route.
 * @param {string}  [image]        Absolute or root-relative social image URL.
 * @param {string}  [type]         Open Graph type. 'website' | 'article' | 'profile'.
 * @param {boolean} [noindex]      Keep the page out of search results.
 * @param {object|object[]} [schema] JSON-LD payload(s) to embed.
 * @param {object}  [article]      Extra og:article fields for blog posts.
 */
const Seo = ({
  title,
  description,
  path,
  image,
  type = 'website',
  noindex,
  schema,
  article,
  children,
}) => {
  const location = useLocation();
  const routePath = path || location.pathname;
  const fallback = getPageSeo(routePath);

  const resolvedTitle = buildTitle(title || fallback.title);
  const resolvedDescription = description || fallback.description || DEFAULT_DESCRIPTION;
  const canonical = absoluteUrl(routePath);
  const resolvedImage = image ? absoluteUrl(image) : SITE.ogImage;
  // Parameterised routes have no PAGE_SEO entry, so the pattern list is what
  // keeps things like /practitioner-detail/:name out of the index.
  const isNoindex =
    noindex ?? fallback.noindex ?? NOINDEX_PATTERNS.some((re) => re.test(routePath));

  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={canonical} />
      {isNoindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}

      {/* Open Graph — what Facebook, LinkedIn, WhatsApp and Slack read. */}
      <meta property="og:site_name" content={SITE.legalName} />
      <meta property="og:locale" content={SITE.locale} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={resolvedImage} />
      {!image && <meta property="og:image:width" content={String(SITE.ogImageWidth)} />}
      {!image && <meta property="og:image:height" content={String(SITE.ogImageHeight)} />}
      <meta property="og:image:alt" content={resolvedTitle} />

      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {article?.author && <meta property="article:author" content={article.author} />}
      {article?.section && <meta property="article:section" content={article.section} />}
      {(article?.tags || []).map((tag) => (
        <meta property="article:tag" content={tag} key={tag} />
      ))}

      {/* Twitter/X reads its own namespace and ignores most og: tags. */}
      <meta name="twitter:card" content={SITE.twitterCard} />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />

      {schemas.map((entry, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <script type="application/ld+json" key={i}>
          {JSON.stringify(entry)}
        </script>
      ))}

      {children}
    </Helmet>
  );
};

export default Seo;
